import { format, parseISO } from 'date-fns';

// Format currency
export const formatCurrency = (amount, currency = '£') => {
  if (amount === null || amount === undefined) return `${currency}0.00`;
  
  const num = parseFloat(amount);
  if (isNaN(num)) return `${currency}0.00`;
  
  return `${currency}${num.toFixed(2)}`;
};

// Format date
export const formatDate = (date, formatString = 'dd/MM/yyyy') => {
  if (!date) return '';
  
  try {
    if (typeof date === 'string') {
      return format(parseISO(date), formatString);
    }
    return format(new Date(date), formatString);
  } catch (error) {
    return '';
  }
};

// Format date for input fields
export const formatDateForInput = (date) => {
  if (!date) return '';
  
  try {
    if (typeof date === 'string') {
      return format(parseISO(date), 'yyyy-MM-dd');
    }
    return format(new Date(date), 'yyyy-MM-dd');
  } catch (error) {
    return '';
  }
};

// Get month name
export const getMonthName = (month) => {
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  return months[month - 1] || '';
};

// Get payment status badge color
export const getPaymentStatusColor = (status) => {
  switch (status) {
    case 'paid':
      return 'bg-success-100 text-success-800';
    case 'partial':
      return 'bg-warning-100 text-warning-800';
    case 'pending':
      return 'bg-danger-100 text-danger-800';
    default:
      return 'bg-secondary-100 text-secondary-800';
  }
};

// Calculate percentage
export const calculatePercentage = (part, total) => {
  if (!total || total === 0) return 0;
  return Math.round((part / total) * 100);
};

// Debounce function
export const debounce = (func, wait, immediate) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      timeout = null;
      if (!immediate) func(...args);
    };
    const callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    if (callNow) func(...args);
  };
};

// Validate email
export const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// Generate months array for dropdowns
export const generateMonthsArray = () => {
  return Array.from({ length: 12 }, (_, i) => ({
    value: i + 1,
    label: getMonthName(i + 1)
  }));
};

// Generate years array for dropdowns
export const generateYearsArray = (startYear = 2020, endYear = new Date().getFullYear() + 1) => {
  const years = [];
  for (let year = endYear; year >= startYear; year--) {
    years.push({ value: year, label: year.toString() });
  }
  return years;
};

// Calculate arrears amount
export const calculateArrears = (amountDue, amountPaid) => {
  const due = parseFloat(amountDue) || 0;
  const paid = parseFloat(amountPaid) || 0;
  return Math.max(0, due - paid);
};

// Get current month and year
export const getCurrentMonthYear = () => {
  const now = new Date();
  return {
    month: now.getMonth() + 1,
    year: now.getFullYear()
  };
};

// Download file from blob
export const downloadFile = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

// Capitalize first letter
export const capitalize = (str) => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
};

// Truncate text
export const truncateText = (text, maxLength = 100) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

// Sort array by property
export const sortBy = (array, property, direction = 'asc') => {
  return [...array].sort((a, b) => {
    let aValue = a[property];
    let bValue = b[property];
    
    // Handle nested properties
    if (property.includes('.')) {
      const props = property.split('.');
      aValue = props.reduce((obj, prop) => obj?.[prop], a);
      bValue = props.reduce((obj, prop) => obj?.[prop], b);
    }
    
    if (aValue < bValue) return direction === 'asc' ? -1 : 1;
    if (aValue > bValue) return direction === 'asc' ? 1 : -1;
    return 0;
  });
};

// Group array by property
export const groupBy = (array, property) => {
  return array.reduce((groups, item) => {
    const key = item[property];
    if (!groups[key]) {
      groups[key] = [];
    }
    groups[key].push(item);
    return groups;
  }, {});
};
